/**
 * EditPlayingXIModal — mid-match Playing XI editor.
 *
 * Two entry points open this modal:
 *   1. Kebab (⋮) → "Edit Playing XI" in BallLayout
 *   2. Tap a player name in the Scorecard "Playing XI" section
 * Both dispatch a `edit-playing-xi:open` window CustomEvent that
 * BallByBallPage listens for.
 *
 * The modal supports three actions per selected player:
 *   - Fix scorer mistake  → PATCH /playing-xi/<team_id>/correct/
 *                           (rewrites Ball / BallWicket rows too)
 *   - Substitution        → routes to existing SubstitutionModal
 *                           (IMPACT / CONCUSSION swap from now onward)
 *   - Edit details only   → position / captain / keeper without swap
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, Pencil, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { getMatchSquad, correctPlayingXI } from "../../api/ballbyball.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";

const MODES = {
  FIX: "fix",
  SUB: "sub",
  EDIT: "edit",
};

export default function EditPlayingXIModal({
  open,
  onClose,
  matchId,
  initialPlayerId,
  onApplied,           // (result) => void
  onOpenSubstitution,  // (type: "IMPACT" | "CONCUSSION") => void
}) {
  const [squad, setSquad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [teamSide, setTeamSide] = useState("team1");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [mode, setMode] = useState(MODES.FIX);
  const [replacementId, setReplacementId] = useState("");
  const [position, setPosition] = useState("");
  const [captain, setCaptain] = useState(false);
  const [keeper, setKeeper] = useState(false);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);

  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose, ref: panelRef });

  // Load squad + XI on open. Also preselect team + player when the
  // caller passed initialPlayerId (from tap-to-edit on the Scorecard).
  useEffect(() => {
    if (!open || !matchId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setConfirming(false);
    getMatchSquad(matchId)
      .then((res) => {
        if (!alive) return;
        setSquad(res.data);
        // Auto-focus the initial player's team if we know it
        let side = "team1";
        if (initialPlayerId) {
          const inTeam1 = (res.data?.team1?.xi || []).some((p) => p.id === initialPlayerId);
          const inTeam2 = (res.data?.team2?.xi || []).some((p) => p.id === initialPlayerId);
          if (inTeam2 && !inTeam1) side = "team2";
        }
        setTeamSide(side);
        setSelectedPlayerId(initialPlayerId || "");
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Failed to load squad"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, matchId, initialPlayerId]);

  const team = squad?.[teamSide];
  const xi = team?.xi || [];
  const reserves = team?.reserves || [];

  const selected = useMemo(
    () => xi.find((p) => p.id === selectedPlayerId) || null,
    [xi, selectedPlayerId],
  );

  // Sync form defaults from the selected player row
  useEffect(() => {
    if (!selected) {
      setPosition("");
      setCaptain(false);
      setKeeper(false);
      setReplacementId("");
      setMode(MODES.FIX);
      return;
    }
    setPosition(String(selected.batting_position ?? ""));
    setCaptain(!!selected.is_captain);
    setKeeper(!!selected.is_wicket_keeper);
    setReplacementId("");
  }, [selected]);

  if (!open) return null;

  const requestConfirm = () => {
    if (!selected) {
      setError("Pick a player from the XI first.");
      return;
    }
    if (mode === MODES.FIX && !replacementId) {
      setError("Pick the correct player from the squad.");
      return;
    }
    if (mode === MODES.SUB) {
      // Route to the existing SubstitutionModal — parent decides IMPACT
      // vs CONCUSSION via the callback.
      onClose();
      onOpenSubstitution?.("CONCUSSION");
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const handleSubmit = async () => {
    if (!selected || !team?.team_id) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        wrong_player_id: selected.id,
        reason: reason || "",
      };
      if (mode === MODES.FIX && replacementId) {
        payload.correct_player_id = replacementId;
      }
      const posNum = position ? Number(position) : null;
      if (posNum && posNum !== selected.batting_position) {
        payload.batting_position = posNum;
      }
      if (captain !== !!selected.is_captain) payload.is_captain = captain;
      if (keeper !== !!selected.is_wicket_keeper) payload.is_wicket_keeper = keeper;

      const res = await correctPlayingXI(matchId, team.team_id, payload);
      if (onApplied) await onApplied(res.data);
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.detail
        || (e?.response?.data ? JSON.stringify(e.response.data) : null)
        || e?.message
        || "Correction failed",
      );
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const replacement = reserves.find((r) => r.id === replacementId);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Edit Playing XI"
        className="bg-bg-primary rounded-2xl max-w-2xl w-full border border-bg-secondary shadow-2xl flex flex-col relative max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
          <div className="flex items-center gap-2 min-w-0">
            <Pencil className="w-5 h-5 text-button-primary" />
            <h3 className="text-base font-semibold text-text-primary">Edit Playing XI</h3>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {loading && <div className="text-text-secondary text-sm">Loading squad…</div>}

          {!loading && squad && (
            <>
              {/* Team picker */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Team
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {["team1", "team2"].map((side) => (
                    <button
                      key={side}
                      onClick={() => { setTeamSide(side); setSelectedPlayerId(""); }}
                      className={`text-left px-3 py-2 rounded border text-sm ${
                        teamSide === side
                          ? "bg-button-primary/15 border-button-primary/50 text-text-primary"
                          : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
                      }`}
                    >
                      {squad[side]?.team_name || side}
                      <span className="block text-[10px] text-text-secondary mt-0.5">
                        {squad[side]?.xi?.length || 0} in XI · {squad[side]?.reserves?.length || 0} reserves
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* XI list — pick a player */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Player in XI
                </label>
                <div className="max-h-52 overflow-y-auto border border-border-default rounded divide-y divide-border-default/60">
                  {xi.length === 0 && (
                    <div className="text-text-secondary text-xs p-3">
                      No XI on file for this team yet.
                    </div>
                  )}
                  {xi.map((p) => {
                    const active = selectedPlayerId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedPlayerId(p.id)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                          active
                            ? "bg-button-primary/15 text-text-primary"
                            : "hover:bg-bg-secondary/60 text-text-secondary"
                        }`}
                      >
                        <span className="min-w-0 truncate">
                          <span className="text-text-secondary text-[11px] mr-2 font-mono">
                            #{p.batting_position}
                          </span>
                          {displayName(p)}
                          {p.is_captain && <span className="ml-1.5 text-[10px] uppercase text-button-primary">(c)</span>}
                          {p.is_wicket_keeper && <span className="ml-1 text-[10px] uppercase text-emerald-300">†</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action panel */}
              {selected && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">
                      What are you doing?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <ModeCard
                        label="Fix scorer mistake"
                        blurb="Wrong player was in XI. Rewrites history."
                        active={mode === MODES.FIX}
                        onClick={() => setMode(MODES.FIX)}
                      />
                      <ModeCard
                        label="Substitution"
                        blurb="Concussion / Impact — swap from now on."
                        active={mode === MODES.SUB}
                        onClick={() => setMode(MODES.SUB)}
                      />
                      <ModeCard
                        label="Edit details only"
                        blurb="Position / captain / keeper flags."
                        active={mode === MODES.EDIT}
                        onClick={() => setMode(MODES.EDIT)}
                      />
                    </div>
                  </div>

                  {mode === MODES.FIX && (
                    <>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[11px] text-amber-300 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Any balls already recorded against{" "}
                          <span className="font-semibold">{displayName(selected)}</span>{" "}
                          (batting, bowling, catches, dismissals) will be reassigned to the correct player. This is irreversible.
                        </span>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                          Correct player (from squad)
                        </label>
                        <select
                          value={replacementId}
                          onChange={(e) => setReplacementId(e.target.value)}
                          className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                          disabled={reserves.length === 0}
                        >
                          <option value="">
                            {reserves.length === 0
                              ? "— no reserves in this team's MatchSquad —"
                              : "— select —"}
                          </option>
                          {reserves.map((r) => (
                            <option key={r.id} value={r.id}>{displayName(r)}</option>
                          ))}
                        </select>
                        {reserves.length === 0 && (
                          <p className="text-[11px] text-text-secondary/70 mt-1">
                            Add the correct player to this team's Match Squad first.
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {mode === MODES.EDIT && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                          Batting position
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={11}
                          value={position}
                          onChange={(e) => setPosition(e.target.value)}
                          className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm px-2 py-1.5 bg-bg-secondary border border-border-default rounded">
                        <input
                          type="checkbox"
                          checked={captain}
                          onChange={(e) => setCaptain(e.target.checked)}
                        />
                        Captain
                      </label>
                      <label className="flex items-center gap-2 text-sm px-2 py-1.5 bg-bg-secondary border border-border-default rounded">
                        <input
                          type="checkbox"
                          checked={keeper}
                          onChange={(e) => setKeeper(e.target.checked)}
                        />
                        Wicket-keeper
                      </label>
                    </div>
                  )}

                  {mode !== MODES.SUB && (
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                        Reason (optional)
                      </label>
                      <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. wrong player picked at toss; noticed in over 6"
                        className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-xs text-rose-300 whitespace-pre-wrap break-words">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-default">
          <button
            onClick={onClose}
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={requestConfirm}
            disabled={saving || loading || !selected}
            className="text-xs uppercase tracking-wider px-4 py-1.5 rounded text-white bg-button-primary hover:bg-button-primary/90 disabled:opacity-40 flex items-center gap-1.5"
          >
            {mode === MODES.SUB ? (
              <>
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Open substitution
              </>
            ) : mode === MODES.FIX ? (
              <>
                <Pencil className="w-3.5 h-3.5" />
                Fix XI
              </>
            ) : (
              <>
                <Pencil className="w-3.5 h-3.5" />
                Save changes
              </>
            )}
          </button>
        </div>

        {/* Confirm overlay */}
        {confirming && selected && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center rounded-2xl p-4">
            <div className="bg-bg-primary rounded-xl max-w-sm w-full border border-border-default shadow-2xl p-5">
              <h4 className="text-base font-semibold text-text-primary mb-2 flex items-center gap-2">
                {mode === MODES.FIX ? (
                  <><AlertTriangle className="w-4 h-4 text-amber-400" /> Confirm correction</>
                ) : (
                  <><Pencil className="w-4 h-4 text-button-primary" /> Confirm changes</>
                )}
              </h4>
              <p className="text-sm text-text-primary leading-snug mb-3">
                {mode === MODES.FIX && replacement ? (
                  <>
                    Replace{" "}
                    <span className="font-semibold text-rose-300">{displayName(selected)}</span>{" "}
                    with{" "}
                    <span className="font-semibold text-emerald-300">{displayName(replacement)}</span>
                    {" "}in the {team?.team_name || "team"} XI.
                    All balls already recorded against the wrong player will be reassigned. This can't be undone.
                  </>
                ) : (
                  <>
                    Save changes to{" "}
                    <span className="font-semibold">{displayName(selected)}</span>
                    {" "}({team?.team_name || "team"} XI)?
                  </>
                )}
              </p>
              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-xs text-rose-300 mb-3 whitespace-pre-wrap break-words">
                  {error}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  disabled={saving}
                  className="text-xs uppercase tracking-wider px-3 py-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary disabled:opacity-40"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className={`text-xs uppercase tracking-wider px-4 py-1.5 rounded text-white disabled:opacity-40 flex items-center gap-1.5 ${
                    mode === MODES.FIX
                      ? "bg-amber-600 hover:bg-amber-700"
                      : "bg-button-primary hover:bg-button-primary/90"
                  }`}
                >
                  {saving ? "Applying…" : "Yes, apply"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModeCard({ label, blurb, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left px-3 py-2 rounded border text-xs leading-snug ${
        active
          ? "bg-button-primary/15 border-button-primary/50 text-text-primary"
          : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
      }`}
    >
      <div className="font-semibold text-sm">{label}</div>
      <div className="text-[11px] text-text-secondary mt-0.5">{blurb}</div>
    </button>
  );
}

function displayName(p) {
  if (!p) return "";
  return p.nick_name
    || `${p.first_name || ""} ${p.last_name || ""}`.trim()
    || p.id;
}
